import { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useJourney } from "../JourneyContext";
import { ContinueButton, StageFrame } from "../StageChrome";
import { PRIORITIES } from "../intentionData";

const POSITIONS = [[9,22],[29,9],[53,12],[76,10],[90,28],[88,62],[70,82],[45,88],[21,81],[6,61],[37,51],[64,50]];

const restoreAssignments = (energy) => Object.entries(energy || {}).flatMap(([key, amount]) => Array.from({ length: amount }, () => key)).slice(0, 10);

export const TradeoffStage = () => {
  const { journey, completeStage } = useJourney();
  const restored = useMemo(() => restoreAssignments(journey.signals.energy), [journey.signals.energy]);
  const [assignments, setAssignments] = useState(() => [...restored, ...Array(10 - restored.length).fill(null)]);
  const [armed, setArmed] = useState(null);
  const fieldRef = useRef(null);
  const nodeRefs = useRef({});

  const allocate = (index, priority) => setAssignments((current) => current.map((item, itemIndex) => itemIndex === index ? priority : item));
  const allocateNext = (priority) => {
    setAssignments((current) => {
      const index = armed ?? current.findIndex((item) => !item);
      return index < 0 ? current : current.map((item, itemIndex) => itemIndex === index ? priority : item);
    });
    setArmed(null);
  };
  const allocateNearest = (index, point) => {
    const nearest = PRIORITIES.map((priority) => {
      const rect = nodeRefs.current[priority]?.getBoundingClientRect();
      return [priority, rect ? Math.hypot(point.x - (rect.left + rect.width / 2), point.y - (rect.top + rect.height / 2)) : Infinity];
    }).sort((a, b) => a[1] - b[1])[0];
    if (nearest[1] < 180) allocate(index, nearest[0]);
  };
  const counts = assignments.reduce((all, item) => item ? { ...all, [item]: (all[item] || 0) + 1 } : all, {});
  const finish = () => completeStage("energy", counts);

  return (
    <StageFrame eyebrow="THE TRADE-OFF" title="You have limited energy." instruction="Where would you place it?" className="tradeoff-stage" testId="stage-3-tradeoff">
      <div ref={fieldRef} className="energy-world" data-testid="stage-3-energy-world">
        <div className="energy-bank" aria-label="Ten energy particles">{assignments.map((target, index) => <motion.button key={index} type="button" drag={!target} dragConstraints={fieldRef} dragMomentum={false} onDragEnd={(_, info) => allocateNearest(index, info.point)} onClick={() => !target && setArmed(index)} aria-pressed={armed === index} aria-label={target ? `Energy placed in ${target}` : `Energy particle ${index + 1}`} className={`energy-particle ${target ? "is-placed" : ""}`} data-testid={`stage-3-energy-particle-${index + 1}`}><i /></motion.button>)}</div>
        {PRIORITIES.map((priority, index) => <button ref={(node) => { nodeRefs.current[priority] = node; }} key={priority} type="button" onClick={() => allocateNext(priority)} className={`energy-receptor ${counts[priority] ? "is-lit" : ""}`} style={{ "--x": `${POSITIONS[index][0]}%`, "--y": `${POSITIONS[index][1]}%`, "--energy": counts[priority] || 0 }} data-testid={`stage-3-priority-${priority.toLowerCase()}`}><span>{priority}</span><b>{counts[priority] || ""}</b><i /></button>)}
        <div className="energy-environment" aria-hidden="true"><i /><i /><i /></div>
        <div className="energy-counter" data-testid="stage-3-energy-counter"><strong>{assignments.filter(Boolean).length}</strong><span>of 10 placed</span></div>
      </div>
      <ContinueButton onClick={finish} disabled={assignments.some((item) => !item)} label="See what your energy reveals" testId="stage-3-continue-button" />
    </StageFrame>
  );
};